import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useChat } from '@/hooks/useChat';
import { Badge } from '@/components/ui/badge';
import { UnifiedMessageBubble, type UnifiedMessage } from './UnifiedMessageBubble';
import { UnifiedMessageInput } from './UnifiedMessageInput';
import { ScrollArea } from '@/components/ui/scroll-area';
import { supabase } from '@/integrations/supabase/client';

interface ChatPanelProps {
  callId?: string;
  roomId?: string;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function ChatPanel({ callId, roomId: initialRoomId, isOpen, onOpenChange }: ChatPanelProps) {
  const { t } = useTranslation("common");
  const [unreadCount, setUnreadCount] = useState(0);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const { messages, isLoading, sendMessage, currentRoom, selectRoom, createDirectRoom } = useChat();

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setCurrentUserId(data.user?.id || null);
    });
  }, []);

  // Select room if provided
  useEffect(() => {
    if (initialRoomId) {
      selectRoom(initialRoomId);
    }
  }, [initialRoomId, selectRoom]);

  // Track unread messages when panel is closed
  useEffect(() => {
    if (!isOpen && messages.length > 0) {
      setUnreadCount((prev) => prev + 1);
    }
  }, [messages.length, isOpen]);

  // Clear unread when opened
  useEffect(() => {
    if (isOpen) {
      setUnreadCount(0);
    }
  }, [isOpen]);

  const handleSendText = async (text: string) => {
    await sendMessage(text);
  };

  const unifiedMessages: UnifiedMessage[] = messages.map((m) => ({
    ...m,
    source: 'e2ee' as const,
  }));

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetTrigger asChild>
        <Button variant="outline" size="icon" className="relative">
          <MessageCircle className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-2 -right-2 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:w-96 flex flex-col p-0">
        <SheetHeader className="p-4 border-b">
          <div className="flex items-center justify-between">
            <SheetTitle className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5" />
              {t("inCallChat")}
            </SheetTitle>
          </div>
        </SheetHeader>

        <ScrollArea className="flex-1 p-4">
          <div className="space-y-2">
            {isLoading ? (
              <p className="text-sm text-muted-foreground text-center py-8">{t("loading")}</p>
            ) : unifiedMessages.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">{t("noMessagesYet")}</p>
            ) : (
              unifiedMessages.map((message) => (
                <UnifiedMessageBubble
                  key={message.id}
                  message={message}
                  isOwn={message.sender_id === currentUserId}
                  currentUserId={currentUserId || undefined}
                />
              ))
            )}
          </div>
        </ScrollArea>

        <UnifiedMessageInput
          onSendText={handleSendText}
          onSendMedia={() => {}}
          onSendVoice={() => {}}
          disabled={!currentRoom}
        />
      </SheetContent>
    </Sheet>
  );
}
